import renderTemplate from './renderTemplate';
import { getGlobalContext } from './context';
import { redact, scrubError, toError } from './scrub';

/**
 * The logging surface the app uses. Serilog-shaped on purpose:
 *
 *   logger.error('Failed to save person {PersonId}', { PersonId: id, Operation: 'SavePerson' }, err);
 *
 * The rendered string becomes the telemetry message; the raw template goes to
 * customDimensions.MessageTemplate, which is the stable grouping key the backend detectors
 * use. Routing matches the server exactly: an error argument produces a trackException (so it
 * lands in `exceptions`, including at Warning severity), no error produces a trackTrace (so it
 * lands in `traces`).
 *
 * This module never imports the SDK. appInsights.js calls setSink() once it is ready, which
 * also drains anything logged before then.
 */

// Matches the SDK's SeverityLevel enum, which happens to be numerically identical to the
// severityLevel values the backend's KQL detectors already filter on (2/3/4).
export const Severity = {
  Verbose: 0,
  Information: 1,
  Warning: 2,
  Error: 3,
  Critical: 4,
};

export const BUFFER_LIMIT = 50;
export const DEDUPE_WINDOW_MS = 5000;

const isProduction = process.env.NODE_ENV === 'production';

let sink = null;
let buffer = [];
let droppedCount = 0;
const recentSends = new Map();

function consoleFallback(level, message, error) {
  /* eslint-disable no-console */
  if (isProduction) return;
  const write =
    level >= Severity.Error
      ? console.error
      : level === Severity.Warning
        ? console.warn
        : console.info;
  write(message, error ?? '');
  /* eslint-enable no-console */
}

/**
 * Drops a repeat of the same signature inside the dedupe window and counts it instead.
 * This is what stops a component that throws on every render from filling the ingestion
 * cap -- realistically the only way a browser SDK does that.
 */
function dedupe(signature) {
  const now = Date.now();
  const seen = recentSends.get(signature);

  if (seen && now - seen.lastSentAt < DEDUPE_WINDOW_MS) {
    seen.suppressed += 1;
    return { send: false, suppressed: 0 };
  }

  const suppressed = seen ? seen.suppressed : 0;
  recentSends.set(signature, { lastSentAt: now, suppressed: 0 });

  // Keep the map from growing without bound in a long-lived session.
  if (recentSends.size > 200) {
    const cutoff = now - DEDUPE_WINDOW_MS * 10;
    recentSends.forEach((value, key) => {
      if (value.lastSentAt < cutoff) recentSends.delete(key);
    });
  }

  return { send: true, suppressed };
}

function emit(entry) {
  if (!sink) {
    if (buffer.length >= BUFFER_LIMIT) {
      buffer.shift();
      droppedCount += 1;
    }
    buffer.push(entry);
    consoleFallback(entry.severityLevel, entry.message, entry.error);
    return;
  }

  const { send, suppressed } = dedupe(entry.signature);
  if (!send) return;

  const properties = { ...entry.properties };
  if (suppressed > 0) properties.SuppressedCount = suppressed;
  if (droppedCount > 0) {
    properties.DroppedLogCount = droppedCount;
    droppedCount = 0;
  }
  if (entry.loggedAt) properties.LoggedAt = entry.loggedAt;

  if (entry.error) {
    sink.trackException({
      exception: entry.error,
      severityLevel: entry.severityLevel,
      properties,
    });
  } else {
    sink.trackTrace({
      message: entry.message,
      severityLevel: entry.severityLevel,
      properties,
    });
  }
}

function write(severityLevel, template, properties, rawError, scopeContext) {
  // logger.debug is a development aid only: console in non-production, never ingested.
  if (severityLevel === Severity.Verbose) {
    if (!isProduction)
      consoleFallback(severityLevel, renderTemplate(template, properties));
    return;
  }

  const error =
    rawError === undefined || rawError === null
      ? null
      : scrubError(toError(rawError).error);

  const merged = {
    ...getGlobalContext(),
    ...scopeContext,
    ...properties,
    MessageTemplate: template,
  };

  emit({
    severityLevel,
    message: redact(renderTemplate(template, properties)),
    error,
    properties: merged,
    signature: `${severityLevel}|${template}|${error ? error.name : ''}`,
    loggedAt: new Date().toISOString(),
  });
}

function createLogger(scopeContext) {
  const api = {
    error: (template, properties, error) =>
      write(Severity.Error, template, properties, error, scopeContext),
    critical: (template, properties, error) =>
      write(Severity.Critical, template, properties, error, scopeContext),
    warn: (template, properties, error) =>
      write(Severity.Warning, template, properties, error, scopeContext),
    info: (template, properties) =>
      write(Severity.Information, template, properties, null, scopeContext),
    debug: (template, properties) =>
      write(Severity.Verbose, template, properties, null, scopeContext),

    event: (name, properties) => {
      const merged = { ...getGlobalContext(), ...scopeContext, ...properties };
      if (sink) sink.trackEvent({ name, properties: merged });
    },

    metric: (name, value, properties) => {
      const merged = { ...getGlobalContext(), ...scopeContext, ...properties };
      if (sink) sink.trackMetric({ name, average: value, properties: merged });
    },

    pageView: (name, uri, properties) => {
      const merged = { ...getGlobalContext(), ...scopeContext, ...properties };
      if (sink) sink.trackPageView({ name, uri, properties: merged });
    },

    /** Returns a logger that merges these dimensions into everything it sends. */
    withContext: (properties) =>
      createLogger({ ...scopeContext, ...properties }),
  };

  return api;
}

export const logger = createLogger(undefined);

/**
 * Called once by appInsights.js. Replays everything buffered before the SDK was ready,
 * oldest first, carrying each item's original timestamp in LoggedAt (the telemetry
 * initializer also applies it to the envelope time).
 */
export function setSink(nextSink) {
  sink = nextSink;
  const pending = buffer;
  buffer = [];
  pending.forEach(emit);
}

export function hasSink() {
  return sink !== null;
}

/** Test hook. */
export function resetLoggerForTests() {
  sink = null;
  buffer = [];
  droppedCount = 0;
  recentSends.clear();
}

/** Test/inspection hook. */
export function getBufferedCount() {
  return buffer.length;
}

export default logger;
